import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["jsonTextarea", "groupsContainer", "template", "visualView", "jsonView", "turnoverInput"];
  static outlets = ["tree-preview"];
  static values = { mode: { type: String, default: "visual" } };
  static DEFAULT_CONNECTORS = ["ac_1", "ac_2", "ac_3", "ac_4", "ac_5", "ac_6", "dc_1", "dc_2"];

  connect() {
    this.handleBeforeCache = () => {
      this.jsonTextareaTarget.value = this.jsonTextareaTarget.defaultValue;
    };
    document.addEventListener("turbo:before-cache", this.handleBeforeCache);
    this.syncGroupsFromJson();
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache);
  }

  toggle(event) {
    const newMode = event.currentTarget.dataset.mode;
    if (newMode === this.modeValue) return;

    if (newMode === "json") {
      this.syncJsonFromGroups();
    } else {
      this.syncGroupsFromJson();
    }

    this.modeValue = newMode;
  }

  modeValueChanged() {
    const isVisual = this.modeValue === "visual";
    this.visualViewTarget.classList.toggle("hidden", !isVisual);
    this.jsonViewTarget.classList.toggle("hidden", isVisual);

    this.element.querySelectorAll("[data-mode-button]").forEach((btn) => {
      const active = btn.dataset.mode === this.modeValue;
      btn.classList.toggle("bg-fill-elevation-level-1", active);
      btn.classList.toggle("text-text-primary", active);
      btn.classList.toggle("shadow-sm", active);
      btn.classList.toggle("text-text-tertiary", !active);
    });
  }

  addGroup() {
    const clone = this.templateTarget.content.cloneNode(true);
    this.groupsContainerTarget.appendChild(clone);
    this.syncJsonFromGroups();
  }

  removeGroup(event) {
    event.currentTarget.closest("[data-group]").remove();
    this.syncJsonFromGroups();
  }

  groupChanged() {
    this.syncJsonFromGroups();
    this.updateDurations();
  }

  syncGroupsFromJson() {
    const textarea = this.jsonTextareaTarget;
    const currentValue = textarea.value;
    const json = currentValue.trim() === "" ? textarea.defaultValue : currentValue;
    textarea.value = json;
    let config;
    try {
      config = JSON.parse(json);
    } catch {
      return;
    }

    const groups = this.sessionsToGroups(config.sessions || []);
    this.renderGroups(groups);
    if (this.hasTurnoverInputTarget && config.turnover_minutes != null) {
      this.turnoverInputTarget.value = config.turnover_minutes;
    }
  }

  syncJsonFromGroups() {
    const groups = this.readGroups();
    const sessions = this.groupsToSessions(groups);
    const config = { sessions };
    if (this.hasTurnoverInputTarget) {
      const parsed = parseInt(this.turnoverInputTarget.value, 10);
      config.turnover_minutes = Number.isFinite(parsed) ? parsed : 30;
    }
    this.jsonTextareaTarget.value = JSON.stringify(config, null, 2);
    this.jsonTextareaTarget.dispatchEvent(new Event("input", { bubbles: true }));
  }

  sessionsToGroups(sessions) {
    const buckets = new Map();

    sessions.forEach((session) => {
      const energy = session.energy_kwh;
      const arrivalHour = session.arrival ? session.arrival[0] + session.arrival[1] / 60 : 8;
      const departureHour = session.departure ? session.departure[0] + session.departure[1] / 60 : 17;
      const day = session.day || "today";
      const key = session.group || `${day}-${energy}-${arrivalHour}-${departureHour}`;

      if (!buckets.has(key)) {
        buckets.set(key, {
          count: 0,
          energy,
          day,
          arrivalMin: arrivalHour,
          arrivalMax: arrivalHour,
          departureMin: departureHour,
          departureMax: departureHour,
        });
      }

      const bucket = buckets.get(key);
      bucket.count += 1;
      bucket.arrivalMin = Math.min(bucket.arrivalMin, arrivalHour);
      bucket.arrivalMax = Math.max(bucket.arrivalMax, arrivalHour);
      bucket.departureMin = Math.min(bucket.departureMin, departureHour);
      bucket.departureMax = Math.max(bucket.departureMax, departureHour);
    });

    return Array.from(buckets.values()).map((b) => ({
      count: b.count,
      energy_kwh: b.energy,
      day: b.day,
      arrival_from: this.decimalToTime(b.arrivalMin),
      arrival_to: this.decimalToTime(Math.max(b.arrivalMax + 0.5, b.arrivalMin + 1)),
      departure_from: this.decimalToTime(b.departureMin),
      departure_to: this.decimalToTime(Math.max(b.departureMax + 0.5, b.departureMin + 1)),
    }));
  }

  groupsToSessions(groups) {
    const connectors = this.availableConnectors();
    let connectorIdx = 0;
    const sessions = [];

    groups.forEach((group, groupIdx) => {
      const groupId = `group_${groupIdx + 1}`;
      const arrivalFromMin = this.timeToMinutes(group.arrival_from);
      const arrivalToMin = this.timeToMinutes(group.arrival_to);
      const arrivalRange = Math.max(arrivalToMin - arrivalFromMin, 0);
      const departureFromMin = this.timeToMinutes(group.departure_from);
      const departureToMin = this.timeToMinutes(group.departure_to);
      const departureRange = Math.max(departureToMin - departureFromMin, 0);

      for (let i = 0; i < group.count; i++) {
        const spread = group.count > 1 ? i / (group.count - 1) : 0;
        const arrivalMinutes = arrivalFromMin + Math.round(arrivalRange * spread);
        const departureMinutes = departureFromMin + Math.round(departureRange * spread);
        const arrival = this.minutesToTimePair(arrivalMinutes);
        const departure = this.minutesToTimePair(departureMinutes);
        const connector = connectors[connectorIdx % connectors.length];
        connectorIdx++;

        sessions.push({
          connector: connector,
          group: groupId,
          day: group.day || "today",
          arrival: arrival,
          departure: departure,
          energy_kwh: group.energy_kwh,
          vehicle: `Voertuig ${sessions.length + 1}`,
          plate: this.generatePlate(sessions.length),
        });
      }
    });

    return sessions;
  }

  minutesToTimePair(minutes) {
    let hour = Math.floor(minutes / 60);
    let min = Math.round((minutes % 60) / 5) * 5;
    if (min >= 60) {
      hour += 1;
      min = 0;
    }
    if (hour >= 24) hour -= 24;
    return [hour, min];
  }

  readGroups() {
    return Array.from(this.groupsContainerTarget.querySelectorAll("[data-group]")).map((el) => ({
      count: parseInt(el.querySelector("[data-field=count]").value) || 1,
      energy_kwh: parseFloat(el.querySelector("[data-field=energy]").value) || 35,
      day: el.querySelector("[data-field=day]")?.value || "today",
      arrival_from: el.querySelector("[data-field=arrival_from]").value || "08:00",
      arrival_to: el.querySelector("[data-field=arrival_to]").value || "09:30",
      departure_from: el.querySelector("[data-field=departure_from]").value || "16:00",
      departure_to: el.querySelector("[data-field=departure_to]").value || "18:00",
    }));
  }

  renderGroups(groups) {
    this.groupsContainerTarget.innerHTML = "";
    groups.forEach((group) => {
      const clone = this.templateTarget.content.cloneNode(true);
      clone.querySelector("[data-field=count]").value = group.count;
      clone.querySelector("[data-field=energy]").value = group.energy_kwh;
      const daySelect = clone.querySelector("[data-field=day]");
      if (daySelect) daySelect.value = group.day || "today";
      clone.querySelector("[data-field=arrival_from]").value = group.arrival_from;
      clone.querySelector("[data-field=arrival_to]").value = group.arrival_to;
      clone.querySelector("[data-field=departure_from]").value = group.departure_from;
      clone.querySelector("[data-field=departure_to]").value = group.departure_to;
      this.groupsContainerTarget.appendChild(clone);
    });
    this.updateDurations();
  }

  updateDurations() {
    this.groupsContainerTarget.querySelectorAll("[data-group]").forEach((el) => {
      const arrFrom = this.timeToMinutes(el.querySelector("[data-field=arrival_from]").value || "08:00");
      const arrTo = this.timeToMinutes(el.querySelector("[data-field=arrival_to]").value || "09:30");
      const depFrom = this.timeToMinutes(el.querySelector("[data-field=departure_from]").value || "16:00");
      const depTo = this.timeToMinutes(el.querySelector("[data-field=departure_to]").value || "18:00");

      const rawShortest = depFrom - arrTo;
      const rawLongest = depTo - arrFrom;
      const shortest = rawShortest < 0 ? rawShortest + 1440 : rawShortest;
      const longest = rawLongest < 0 ? rawLongest + 1440 : rawLongest;
      const label = el.querySelector("[data-field=duration]");
      if (!label) return;

      const fmt = (mins) => {
        const hours = Math.ceil((mins / 60) * 2) / 2;
        return hours % 1 === 0 ? `${hours}h` : `${hours.toFixed(1)}h`;
      };

      label.textContent = shortest === longest ? fmt(longest) : `${fmt(shortest)}–${fmt(longest)}`;
    });
  }

  availableConnectors() {
    if (!this.hasTreePreviewOutlet) return this.constructor.DEFAULT_CONNECTORS;

    try {
      const treeTextarea = this.treePreviewOutlet.treeDataTarget;
      const treeData = JSON.parse(treeTextarea.value);
      const connectors = this.extractConnectors(treeData.children || []);
      return connectors.length > 0 ? connectors : this.constructor.DEFAULT_CONNECTORS;
    } catch {
      return this.constructor.DEFAULT_CONNECTORS;
    }
  }

  extractConnectors(children) {
    const connectors = [];
    children.forEach((child) => {
      if (child.type === "connector") {
        connectors.push(child.name.toLowerCase().replace(/\s+/g, "_"));
      }
      if (child.children) connectors.push(...this.extractConnectors(child.children));
    });
    return connectors;
  }

  decimalToTime(decimal) {
    let hours = Math.floor(decimal) % 24;
    let minutes = Math.round(((decimal % 1) * 60) / 5) * 5;
    if (minutes >= 60) {
      hours = (hours + 1) % 24;
      minutes = 0;
    }
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  timeToMinutes(timeStr) {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  }

  generatePlate(index) {
    const letters = "ABCDEFGHJKLMNPRSTUVWXYZ";
    const a = letters[index % letters.length];
    const b = letters[(index + 7) % letters.length];
    return `${a}${b}-${100 + index}-${b}${a}`;
  }
}
